import { Controller } from "@hotwired/stimulus"
import { Dropdown, Modal } from "bootstrap"
import {
  NESTED_BACKDROP_OFFSET,
  NESTED_MODAL_OFFSET,
  topmostOpenModalZIndex
} from "../../src/shared/helpers/modal_stacking"

export default class extends Controller {
  switch (event) {
    event.preventDefault()

    const targetModal = this.findTargetModal(event)
    if (!targetModal) return

    const currentModal = this.element.closest(".modal")
    if (currentModal === targetModal) return

    this.relocateNestedModal(targetModal, currentModal)
    this.ensureModalInBody(targetModal)

    const openTargetModal = () => {
      Modal.getOrCreateInstance(targetModal).show()
    }

    if (currentModal?.classList.contains("show")) {
      currentModal.addEventListener("hidden.bs.modal", openTargetModal, { once: true })
      Modal.getOrCreateInstance(currentModal).hide()
    } else {
      openTargetModal()
    }
  }

  // Practitioner profile tabs share one open shell. Swap title, body, and footer
  // in place so Bootstrap does not play the close-then-open animation.
  switchPanel (event) {
    event.preventDefault()

    const panelModalId = event.params.modalId
    const currentModal = this.element.closest(".modal")
    if (!panelModalId || !currentModal?.classList.contains("show")) return

    this.stampModal(currentModal)
    this.stampModal(document.getElementById(panelModalId))

    const currentContent = this.panelContent(currentModal)
    if (!currentContent || currentContent.dataset.panelModalId === panelModalId) return

    const targetContent = this.findPanelContent(panelModalId)
    const currentDialog = currentContent.parentElement
    const targetDialog = targetContent?.parentElement
    if (!targetContent || !currentDialog || !targetDialog) return

    this.bindPanelFields(currentContent)
    this.bindPanelFields(targetContent)
    targetDialog.appendChild(currentContent)
    currentDialog.appendChild(targetContent)
    this.applyDialogClass(currentDialog, targetContent)
    this.applyDialogClass(targetDialog, currentContent)
    this.syncLabel(currentModal)
    this.syncLabel(targetDialog.closest(".modal"))
    this.resetBodyScroll(currentModal)
    this.loadLazyFrames(currentModal)
    this.ensureRestoreOnHide(currentModal)
    currentModal.focus()
  }

  // Open a nested modal on top of the current one (keeps parent open).
  openFromNested (event) {
    event.preventDefault()
    event.stopPropagation()

    const targetModal = this.findTargetModal(event)
    if (!targetModal) return

    // Controller often lives on a shell turbo-frame that *wraps* the .modal
    // (frame > form > .modal), so this.element.closest(".modal") is null.
    // Prefer the trigger's modal, then the topmost open modal for z-index stacking.
    const currentModal = this.element.closest(".modal") ||
      event.currentTarget.closest(".modal") ||
      this.topmostOpenModal(targetModal)
    if (currentModal === targetModal) return

    this.hideTriggerDropdown(event.currentTarget)

    const moved = this.ensureModalInBody(targetModal)
    if (moved) Modal.getInstance(targetModal)?.dispose()

    // Wait for layout after DOM relocation — showing immediately throws in Bootstrap
    // (Cannot read properties of null reading 'style') on the first open.
    this.showModalAfterLayout(targetModal, moved, currentModal)
  }

  // Open a sibling modal on top of the current one and leave it in place.
  // Bootstrap's data-bs-toggle would hide the parent once its show transition
  // ends, and openFromNested would move the target out of the container that
  // Turbo streams replace.
  openStacked (event) {
    event.preventDefault()

    const targetModal = this.findTargetModal(event)
    if (!targetModal) return

    const currentModal = event.currentTarget.closest(".modal")
    if (currentModal === targetModal) return

    this.ensureScrollLockOnHide(targetModal)
    this.showModalAfterLayout(targetModal, false, currentModal)
  }

  // Bootstrap removes body.modal-open when any modal hides, even with the parent
  // still open behind it, so the page under the parent would scroll again.
  ensureScrollLockOnHide (modal) {
    if (modal.dataset.stackedScrollLockBound) return

    modal.dataset.stackedScrollLockBound = "true"
    modal.addEventListener("hidden.bs.modal", (event) => {
      if (event.target !== modal || !document.querySelector(".modal.show")) return

      document.body.classList.add("modal-open")
      if (!document.body.style.overflow) document.body.style.overflow = "hidden"
    })
  }

  topmostOpenModal (exclude = null) {
    const openModals = [...document.querySelectorAll(".modal.show")]
      .filter((modal) => modal !== exclude)

    return openModals.at(-1) ?? null
  }

  findTargetModal (event) {
    const targetModalId = event.params.modalId
    if (!targetModalId) return null

    return document.getElementById(targetModalId)
  }

  hideTriggerDropdown (trigger) {
    const dropdownToggle = trigger.closest(".dropdown")?.querySelector('[data-bs-toggle="dropdown"]')
    if (!dropdownToggle) return

    Dropdown.getInstance(dropdownToggle)?.hide()
  }

  showModalAfterLayout (targetModal, waitForLayout, currentModal = null) {
    const show = () => {
      try {
        this.stackAboveParentModal(targetModal, currentModal)
        Modal.getOrCreateInstance(targetModal).show()
      } catch (error) {
        console.error("modal--navigation#openFromNested failed to show modal", error)
      }
    }

    if (!waitForLayout) {
      show()
      return
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(show)
    })
  }

  // Bootstrap does not stack nested modals; without a higher z-index the child
  // opens behind the parent backdrop and looks like a dead click.
  // Portaled overlays (Tom Select, …) use PORTALED_OVERLAY_OFFSET above this stack.
  stackAboveParentModal (targetModal, _currentModal) {
    const openModals = [...document.querySelectorAll(".modal.show")]
      .filter((modal) => modal !== targetModal)
    if (!openModals.length) return

    const parentZ = topmostOpenModalZIndex(targetModal)
    targetModal.style.zIndex = String(parentZ + NESTED_MODAL_OFFSET)

    targetModal.addEventListener("shown.bs.modal", () => {
      const backdrops = document.querySelectorAll(".modal-backdrop")
      const lastBackdrop = backdrops[backdrops.length - 1]
      if (lastBackdrop) lastBackdrop.style.zIndex = String(parentZ + NESTED_BACKDROP_OFFSET)
    }, { once: true })
  }

  // Returns true when the modal root was moved to document.body.
  ensureModalInBody (targetModal) {
    const relocatable = this.relocatableRoot(targetModal)

    if (relocatable.parentElement === document.body) return false

    document.body.appendChild(relocatable)
    return true
  }

  // Move dedicated modal turbo-frames (create/manage shells) with the modal.
  //
  // Never relocate a content/list turbo-frame that also hosts tables/toolbars —
  // that would empty the parent modal (e.g. site sessions → appointments).
  //
  // Companion modals (create-profile, WhatsApp picker, …) must be SIBLINGS of the
  // shell frame, not children. If they sit inside the shell, openFromNested moves
  // a huge subtree and Bootstrap .show() fails silently ("Créer …" does nothing).
  // See workflows/admin-legacy-to-v2.mdc — "Create / manage modal shell vs companion modals".
  relocatableRoot (targetModal) {
    const turboFrame = targetModal.closest("turbo-frame")
    if (turboFrame && this.isDedicatedModalFrame(turboFrame, targetModal)) {
      return turboFrame
    }

    if (turboFrame && this.frameOnlyWrapsModal(turboFrame, targetModal)) {
      return turboFrame
    }

    return targetModal.closest("form") || targetModal
  }

  // Modal shell frames wrap a single manage/create form. List frames also host
  // toolbars/tables and must stay inside the parent modal.
  isDedicatedModalFrame (frame, targetModal) {
    if (!frame.contains(targetModal)) return false

    return !frame.querySelector(
      ".table-create-toolbar, .tablev2__container, .tablev2__empty-state, table.tablev2"
    )
  }

  frameOnlyWrapsModal (frame, targetModal) {
    return ![...frame.querySelectorAll("*")].some((element) => (
      element !== targetModal &&
      !targetModal.contains(element) &&
      !element.contains(targetModal) &&
      !element.matches("script, template, style")
    ))
  }

  relocateNestedModal (targetModal, currentModal) {
    if (!currentModal?.contains(targetModal)) return

    const destination = document.querySelector(".tablev2__modals-container") || document.body
    destination.appendChild(targetModal)
  }

  panelContent (modal) {
    const dialog = modal.querySelector(".modal-dialog")
    if (!dialog) return null

    return [...dialog.children].find((child) => child.classList.contains("modal-content")) || null
  }

  stampModal (modal) {
    if (!modal) return

    const content = this.panelContent(modal)
    if (!content || content.dataset.panelModalId) return

    content.dataset.panelModalId = modal.id
    if (!content.dataset.dialogClass) content.dataset.dialogClass = content.parentElement?.className || ""
  }

  findPanelContent (panelModalId) {
    return document.querySelector(`.modal-content[data-panel-modal-id="${panelModalId}"]`)
  }

  applyDialogClass (dialog, content) {
    if (content.dataset.dialogClass) dialog.className = content.dataset.dialogClass
  }

  // The edit form stays put. Fields that move with the panel keep submitting it
  // via the form attribute, including controls added before the next save.
  bindPanelFields (content) {
    const formId = content.dataset.modalFormId
    if (!formId) return

    const form = document.getElementById(formId)
    if (!form) return

    this.tagFormControls(content, form)
    if (form.dataset.panelFieldsBound) return

    form.dataset.panelFieldsBound = "true"
    form.addEventListener("submit", () => {
      const panel = document.querySelector(`.modal-content[data-modal-form-id="${formId}"]`)
      if (panel) this.tagFormControls(panel, form)
    })
  }

  tagFormControls (content, form) {
    content.querySelectorAll("input, select, textarea, button").forEach((control) => {
      const nestedForm = control.closest("form")
      if (nestedForm && nestedForm !== form) return

      control.setAttribute("form", form.id)
    })
  }

  syncLabel (modal) {
    const titleId = modal.querySelector(".modal-title")?.id
    if (titleId) modal.setAttribute("aria-labelledby", titleId)
  }

  resetBodyScroll (modal) {
    const body = modal.querySelector(".modal-body")
    if (body) body.scrollTop = 0
  }

  // Lazy frames load on shown.bs.modal. An in-place tab never shows the other
  // modal, so start the request when its panel becomes visible.
  loadLazyFrames (modal) {
    modal.querySelectorAll("turbo-frame[data-lazy-src]").forEach((turboFrame) => {
      const lazySrc = turboFrame.dataset.lazySrc
      if (!lazySrc) return

      if (turboFrame.getAttribute("src") === lazySrc) turboFrame.removeAttribute("src")

      turboFrame.src = lazySrc
    })
  }

  ensureRestoreOnHide (shell) {
    if (shell.dataset.panelRestoreBound) return

    shell.dataset.panelRestoreBound = "true"
    shell.addEventListener("hidden.bs.modal", (event) => {
      if (event.target !== shell) return

      this.restorePanels()
    })
  }

  restorePanels () {
    document.querySelectorAll(".modal-content[data-panel-modal-id]").forEach((content) => {
      const modal = document.getElementById(content.dataset.panelModalId)
      const dialog = modal?.querySelector(".modal-dialog")
      if (!dialog || content.parentElement === dialog) return

      dialog.appendChild(content)
      if (content.dataset.dialogClass) dialog.className = content.dataset.dialogClass
      modal.setAttribute("aria-labelledby", `${modal.id}-label`)
    })
  }
}
